import type { Middleware } from '@reduxjs/toolkit';
import { createSnapshot } from './snapshotSerializer';
import historyManager from './historyManager';
import {
  addWidgetToRecipeAction,
  setGatePosition,
  setWidgetDimensions,
  toggleWidgetState,
  connectGates,
  disconnectGate,
  addWidgetFromTemplateAction,
  addHubServiceWidgetAction,
} from '@src/features/LogicMapper/logicMapperSlice';
import { pasteFromClipboardAction } from '@src/features/LogicMapper/reducers/clipboardReducers';
import { removeWidgetsAction } from '@src/features/LogicMapper/reducers/removeWidgetsReducer';
import duplicateWidgetReducer from '@src/features/LogicMapper/reducers/duplicateWidgetReducer';
import { setRecipeName, setRecipeMetaAction, pushFolderPath, setFolderPath, gotoFolderPathId, gotoBlockRootAction, replaceCurrentFolderPathAction } from '@src/features/Workspace/workspaceSlice';
import { storeRecipeAction } from '@src/features/interface/reducers/storeRecipeReducer';
import { widgetStateChanged } from '@src/features/uiSignals/actions';
import { setCanvasZoom } from '@src/features/LogicMapper/logicMapperSlice';
import { isHistoryEnabled } from './historyToggle';

/**
 * Returns the set of action types that should trigger a snapshot capture after reducers have committed.
 * Keep this list small at first; extend coverage in later tasks.
 */
const getCaptureActionTypes = (): Set<string> => {
  return new Set<string>([
    setGatePosition.type,
    setWidgetDimensions.type,
    widgetStateChanged.type,
    setCanvasZoom.type,
    pushFolderPath.type,
    setFolderPath.type,
    gotoFolderPathId.type,
    gotoBlockRootAction.type,
    replaceCurrentFolderPathAction.type,
    toggleWidgetState.type,
    connectGates.type,
    disconnectGate.type,
    
    addWidgetToRecipeAction.fulfilled.type,
    pasteFromClipboardAction.fulfilled.type,
    removeWidgetsAction.fulfilled.type,
    duplicateWidgetReducer.duplicateWidgetAction.fulfilled.type,
    addWidgetFromTemplateAction.fulfilled.type,
    addHubServiceWidgetAction.fulfilled.type,

    setRecipeName.type,
    setRecipeMetaAction.type,
  ]);
};

// Actions to be coalesced into a single snapshot within a short window
const getCoalescedActionTypes = (): Set<string> => {
  return new Set<string>([
    setGatePosition.type,
    setWidgetDimensions.type,
    widgetStateChanged.type,
    setCanvasZoom.type,
    // Optional: coalesce rapid multi-adds into one entry
    pasteFromClipboardAction.fulfilled.type,
    addWidgetFromTemplateAction.fulfilled.type,
    addHubServiceWidgetAction.fulfilled.type,
  ]);
};

// Debounce window in ms for coalesced snapshots
const COALESCE_WINDOW_MS = 300;
// Per-recipe pending timers
const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * History capture middleware.
 * - Captures a snapshot after mutating actions commit
 * - Skips capture when restoring
 */
const historyMiddleware: Middleware<any, any> = (storeApi) => (next) => (action) => {
  // Track save lifecycle to suppress capture during save
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (historyMiddleware as any)._saving = (historyMiddleware as any)._saving || false;
  if (action.type === storeRecipeAction.pending.type) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (historyMiddleware as any)._saving = true;
  }
  if (action.type === storeRecipeAction.fulfilled.type || action.type === storeRecipeAction.rejected.type) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (historyMiddleware as any)._saving = false;
  }
  const captureTypes = getCaptureActionTypes();
  const coalesceTypes = getCoalescedActionTypes();

  // Commit state change first
  const result = next(action);

  // Skip if restoring or saving
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!isHistoryEnabled() || historyManager.isRestoring() || (historyMiddleware as any)._saving === true) {
    return result;
  }

  // Only capture if this action is in the allowlist
  if (!captureTypes.has(action.type)) {
    return result;
  }

  const state = storeApi.getState();
  const recipePoolId = state.workspace.currentRecipe.poolId;
  if (!recipePoolId) {
    return result;
  }

  const performCapture = (): void => {
    try {
      const latest = storeApi.getState();
      const pid = latest.workspace.currentRecipe.poolId;
      if (!pid) { return; }
      // If this is the very first mutating action and we have no past baseline, capture baseline first
      const stateRef = historyManager.getState?.();
      if (stateRef && stateRef.pastSize === 0) {
        const baseline = createSnapshot({ recipePoolId: pid, state: latest });
        historyManager.capture(pid, baseline);
      }
      const snapshot = createSnapshot({ recipePoolId: pid, state: latest });
      historyManager.capture(pid, snapshot);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[historyMiddleware] Failed to capture snapshot:', e);
    }
  };

  if (coalesceTypes.has(action.type)) {
    // Debounce per-recipe
    const existing = pendingTimers.get(recipePoolId);
    if (existing) {
      clearTimeout(existing);
    }
    const timer = setTimeout(() => {
      pendingTimers.delete(recipePoolId);
      performCapture();
    }, COALESCE_WINDOW_MS);
    pendingTimers.set(recipePoolId, timer);
  } else {
    // Flush any pending coalesced capture first to preserve ordering
    const existing = pendingTimers.get(recipePoolId);
    if (existing) {
      clearTimeout(existing);
      pendingTimers.delete(recipePoolId);
    }
    performCapture();
  }

  return result;
};

export default historyMiddleware;